/* =====================================================================
 * ChartCanvas — DrawingML 차트(c:chartSpace) 근사 렌더 (bar/line/pie/doughnut)
 *
 * 데이터·시리즈 색(그라데이션 포함)·데이터 라벨(dLbls)·값 축 눈금/그리드
 * (majorGridlines)·범례(legend)를 SVG 로 근사한다. 색 미지정 시 accent1~6 순환
 * (Office 자동 색 규칙 근사). 미지원 차트 종류는 연회색 자리표시자
 * (불완전 렌더보다 폴백 원칙).
 *
 * 차트 파트 문법은 PPTX·XLSX 가 공유하므로 이 컴포넌트는 두 렌더러가 함께 쓴다.
 * 포맷별 차이(테마 색 출처, 제목 표시 여부)는 props 로만 들어온다.
 * ===================================================================== */

import React, { useId, useMemo } from 'react';
import type { PptxChart, PptxChartSeries } from '@auw/workspace-utils';
import { resolveFontFallback } from '@auw/workspace-utils';
import { chartBarGeometry } from './chartBarGeometry';

export interface ChartCanvasProps {
  chart: PptxChart | undefined;
  /** 차트 영역 크기 (px) */
  width: number;
  height: number;
  /** 테마 accent1~6 — 시리즈/조각 색이 없을 때 순환한다. */
  accents: string[];
  /**
   * c:title 을 상단에 그릴지. PPTX 는 제목을 별도 텍스트 도형으로 두는 경우가
   * 많아 기본은 끔이고, 제목이 차트 파트 안에만 있는 XLSX 에서 켠다.
   */
  showTitle?: boolean;
}

const AXIS_COLOR = '#bfbfbf';
const GRID_COLOR = '#d9d9d9';
const LABEL_COLOR = '#595959';
const LEADER_COLOR = '#a6a6a6';
const TITLE_COLOR = '#404040';

/** pt → px (96dpi 기준 4/3) — 미지정 시 폴백 */
function ptPx(pt: number | undefined, fallback: number): number {
  return pt !== undefined ? (pt * 4) / 3 : fallback;
}

/** 시리즈/포인트 단색 — 명시 색 → 테마 accent 순환 */
function seriesColor(chart: PptxChart, serIdx: number, ptIdx: number, accents: string[]): string {
  const ser = chart.series[serIdx];
  if (chart.type === 'pie' || chart.type === 'doughnut') {
    return ser.pointColors?.[ptIdx] ?? ser.color ?? accents[ptIdx % accents.length];
  }
  return ser.color ?? accents[serIdx % accents.length];
}

/**
 * numFmt(formatCode) 최소 해석 — 실덱 빈도 상위만.
 * '0%' 류 → 백분율, '$' 포함 → 달러 2자리, General → 요약 표기.
 */
function formatValue(v: number, fmt?: string): string {
  if (fmt && fmt.includes('%')) {
    const pct = v * 100;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  }
  if (fmt && fmt.includes('$')) {
    return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (Number.isInteger(v)) return String(v);
  return String(Math.round(v * 100) / 100);
}

/** "깔끔한" 축 스텝 — 1/2/2.5/5 × 10^k 중 target 분할에 가장 가까운 값 */
function niceStep(range: number, targetTicks = 6): number {
  if (range <= 0) return 1;
  const rough = range / targetTicks;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (rough <= m * pow) return m * pow;
  }
  return 10 * pow;
}

/** 파이/도넛에서 값이 비율(합≈1)일 때 % 표기 폴백 (셀 서식을 읽을 수 없는 경우) */
function inferPieFormat(values: number[]): string | undefined {
  const sum = values.reduce((s, v) => s + Math.abs(v), 0);
  return Math.abs(sum - 1) < 0.02 ? '0%' : undefined;
}

export const ChartCanvas: React.FC<ChartCanvasProps> = ({
  chart, width, height, accents, showTitle = false,
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const title = showTitle ? chart?.title : undefined;
  const titleFs = title ? Math.max(9, Math.min(15, height * 0.06)) : 0;
  // 제목 밴드를 위에서 떼어내고 나머지 높이로 본문을 계산한다.
  const titleH = title ? titleFs * 1.9 : 0;
  const w = width;
  const h = height - titleH;

  const content = useMemo((): React.ReactNode => {
    if (!chart || w <= 0 || h <= 0) return null;
    const fontSize = Math.max(8, Math.min(12, h * 0.042));
    // dLbls 에 numFmt 미지정(sourceLinked=셀 서식)이면 값 축 numFmt 로 근사
    const labelFmt = (ser: PptxChartSeries) =>
      ser.dataLabels?.numFmt ?? chart.dataLabels?.numFmt ?? chart.valAxis?.numFmt;
    const showVal = (ser: PptxChartSeries) =>
      ser.dataLabels?.showVal ?? chart.dataLabels?.showVal ?? false;
    const labelColor = (ser: PptxChartSeries, fallback: string) =>
      ser.dataLabels?.color ?? chart.dataLabels?.color ?? fallback;
    const labelFs = (ser: PptxChartSeries, fallback: number) =>
      ptPx(ser.dataLabels?.sizePt ?? chart.dataLabels?.sizePt, fallback);
    const labelWeight = (ser: PptxChartSeries) =>
      (ser.dataLabels?.bold ?? chart.dataLabels?.bold) ? 700 : 400;
    const leaderOn = (ser: PptxChartSeries) =>
      ser.dataLabels?.showLeaderLines ?? chart.dataLabels?.showLeaderLines ?? false;

    /* ---- 범례 — 하단 밴드(b/t) 또는 측면 컬럼(r/l) --------------------- */
    const legendFs = ptPx(chart.legendSizePt ?? 10, fontSize * 0.85);
    const legendSide = chart.legendPos === 'r' || chart.legendPos === 'l';
    const legendLabels: string[] = chart.type === 'pie' || chart.type === 'doughnut'
      ? (chart.series[0]?.categories ?? [])
      : chart.series.map((s, i) => s.name ?? `계열 ${i + 1}`);
    const maxLegendChars = legendLabels.reduce((m, s) => Math.max(m, (s ?? '').length), 0);
    const legendW = chart.legendPos && legendSide
      ? legendFs * (0.62 * maxLegendChars) + legendFs * 2
      : 0;
    const legendH = chart.legendPos && !legendSide ? legendFs * 2.2 : 0;
    const legendTop = chart.legendPos === 't' ? legendH : 0;

    const buildLegend = (entries: { label: string; color: string }[]): React.ReactNode => {
      if (!chart.legendPos || entries.length === 0) return null;
      const textFill = chart.legendColor ?? LABEL_COLOR;
      const weight = chart.legendBold ? 700 : 400;
      if (legendSide) {
        const rowH = legendFs * 1.7;
        const y0 = (h - entries.length * rowH) / 2;
        const x0 = chart.legendPos === 'l' ? 4 : w - legendW + 4;
        return (
          <g key="legend">
            {entries.map((e, i) => (
              <g key={i}>
                <rect x={x0} y={y0 + i * rowH + rowH / 2 - legendFs * 0.35}
                  width={legendFs * 0.7} height={legendFs * 0.7} fill={e.color} />
                <text x={x0 + legendFs * 1.1} y={y0 + i * rowH + rowH / 2 + legendFs * 0.35}
                  fontSize={legendFs} fontWeight={weight} fill={textFill}>
                  {e.label}
                </text>
              </g>
            ))}
          </g>
        );
      }
      const itemW = Math.min(w / entries.length, legendFs * 11);
      const totalW = itemW * entries.length;
      const x0 = (w - totalW) / 2;
      const y = chart.legendPos === 't' ? legendH / 2 : h - legendH / 2;
      return (
        <g key="legend">
          {entries.map((e, i) => (
            <g key={i}>
              <rect x={x0 + i * itemW} y={y - legendFs * 0.55} width={legendFs * 0.7} height={legendFs * 0.7} fill={e.color} />
              <text x={x0 + i * itemW + legendFs * 1.1} y={y + fontSize * 0.15} fontSize={legendFs}
                fontWeight={weight} fill={textFill}>
                {e.label}
              </text>
            </g>
          ))}
        </g>
      );
    };

    /* ---- 파이 / 도넛 -------------------------------------------------- */
    if (chart.type === 'pie' || chart.type === 'doughnut') {
      const ser = chart.series[0];
      const vals = ser.values.map((v) => v ?? 0);
      const total = vals.reduce((s, v) => s + Math.abs(v), 0);
      if (total <= 0) return null;
      const availW = w - legendW;
      const cx = chart.legendPos === 'l' ? legendW + availW / 2 : availW / 2;
      const cy = legendTop + (h - legendH) / 2;
      const r = Math.min(availW, h - legendH) / 2 * 0.86;
      const rIn = chart.type === 'doughnut' ? r * ((chart.holeSize ?? 50) / 100) : 0;
      const fmt = labelFmt(ser) ?? inferPieFormat(vals);
      let angle = -90;
      const segs: React.ReactNode[] = [];
      const labels: React.ReactNode[] = [];
      vals.forEach((v, i) => {
        const sweep = (Math.abs(v) / total) * 360;
        if (sweep <= 0) return;
        const a0 = (angle * Math.PI) / 180;
        const a1 = ((angle + Math.min(sweep, 359.98)) * Math.PI) / 180;
        const large = sweep > 180 ? 1 : 0;
        const p = (rr: number, a: number) => `${(cx + rr * Math.cos(a)).toFixed(2)},${(cy + rr * Math.sin(a)).toFixed(2)}`;
        const d = rIn > 0
          ? `M${p(r, a0)} A${r},${r} 0 ${large} 1 ${p(r, a1)} L${p(rIn, a1)} A${rIn},${rIn} 0 ${large} 0 ${p(rIn, a0)} Z`
          : `M${cx},${cy} L${p(r, a0)} A${r},${r} 0 ${large} 1 ${p(r, a1)} Z`;
        segs.push(<path key={i} d={d} fill={seriesColor(chart, 0, i, accents)} />);
        // 데이터 라벨 — 기본 위치=조각 중앙(중간 반경), manualLayout 오프셋은
        // 차트영역 w/h 비율로 가산. 오프셋으로 이동한 라벨엔 꺾인 리더선.
        if (showVal(ser)) {
          const mid = ((angle + sweep / 2) * Math.PI) / 180;
          const fs = labelFs(ser, fontSize);
          const rMid = rIn > 0 ? (r + rIn) / 2 : r * 0.72;
          const off = ser.dataLabels?.offsets?.[i];
          const lx = cx + rMid * Math.cos(mid) + (off ? off.dx * w : 0);
          const ly = cy + rMid * Math.sin(mid) + (off ? off.dy * h : 0);
          const text = formatValue(v, fmt);
          if (off && leaderOn(ser)) {
            const arcX = cx + r * Math.cos(mid);
            const arcY = cy + r * Math.sin(mid);
            const half = text.length * fs * 0.3;
            const sign = lx >= arcX ? 1 : -1;      // 라벨이 오른쪽이면 왼쪽 변으로 진입
            const entryX = lx - sign * (half + 2);
            const elbowX = entryX - sign * fs * 0.7;
            labels.push(<polyline key={`ld${i}`}
              points={`${arcX.toFixed(1)},${arcY.toFixed(1)} ${elbowX.toFixed(1)},${ly.toFixed(1)} ${entryX.toFixed(1)},${ly.toFixed(1)}`}
              fill="none" stroke={LEADER_COLOR} strokeWidth={1} />);
          }
          labels.push(
            <text key={`l${i}`} x={lx} y={ly + fs * 0.35}
              fontSize={fs} fill={labelColor(ser, '#ffffff')}
              fontWeight={labelWeight(ser)} textAnchor="middle">
              {text}
            </text>,
          );
        }
        angle += sweep;
      });
      const legend = buildLegend(
        ser.categories.map((c, i) => ({ label: c, color: seriesColor(chart, 0, i, accents) })),
      );
      return [...segs, ...labels, legend];
    }

    /* ---- 막대 / 라인 공통 스케일 -------------------------------------- */
    const catCount = Math.max(...chart.series.map((s) => s.values.length), 1);
    const { stacked, segments, dataMax, dataMin } = chartBarGeometry(chart);
    const ax = chart.valAxis;
    // LO 실측 눈금 밀도: 세로 값축 ≈9분할 (18000→2000, 6000→1000, 0.35→0.05, 5→0.5)
    const step = ax?.majorUnit ?? niceStep((ax?.max ?? dataMax) - (ax?.min ?? Math.min(dataMin, 0)), 9);
    // LO 축 최대: 데이터 최대 + 범위의 ~10% 헤드룸 후 스텝 올림
    // (5000→6000, 0.30→0.35, 16226→18000, 4.3→5.0 전부 정합)
    const axMax = ax?.max
      ?? Math.ceil((dataMax + (dataMax - Math.min(dataMin, 0)) * 0.1) / step) * step;
    const axMin = ax?.min ?? Math.floor((Math.min(dataMin, 0)) / step) * step;
    const range = axMax - axMin || 1;
    const ticks: number[] = [];
    for (let t = axMin; t <= axMax + step * 0.001; t += step) ticks.push(Math.round(t * 1e9) / 1e9);

    const showAxisLabels = !!ax?.majorGridlines || !!ax?.numFmt;
    // 축 텍스트 스타일 — txPr sz/b 반영 (없으면 차트 기본 10pt)
    const valFs = ptPx(ax?.sizePt ?? 10, fontSize * 0.9);
    const valWeight = ax?.bold ? 700 : 400;
    const catFs = ptPx(chart.catAxis?.sizePt ?? 10, fontSize);
    const catWeight = chart.catAxis?.bold ? 700 : 400;
    // 축 텍스트/그리드 색 — txPr·majorGridlines spPr 명시색 우선 (다크 배경 대응)
    const valColor = ax?.color ?? LABEL_COLOR;
    const catColor = chart.catAxis?.color ?? LABEL_COLOR;
    const valGrid = ax?.gridColor ?? GRID_COLOR;
    const catGrid = chart.catAxis?.gridColor ?? GRID_COLOR;
    const tickStrs = ticks.map((t) => formatValue(t, ax?.numFmt));
    const maxTickChars = tickStrs.reduce((m, s) => Math.max(m, s.length), 0);
    // 거터 폭 = 가장 긴 눈금 문자열 실측 근사 ($6,000.00 잘림 방지)
    const axisLabelW = showAxisLabels && chart.barDir !== 'bar'
      ? valFs * (0.62 * maxTickChars + 0.6)
      : 0;
    const padTop = chart.plotLayout ? chart.plotLayout.y * h : h * 0.05 + legendTop;
    const catLabelH = catFs * 2;
    const plotH = chart.plotLayout ? chart.plotLayout.height * h : h - padTop - catLabelH - (chart.legendPos === 'b' ? legendH : 0);
    const plotX = chart.plotLayout ? chart.plotLayout.x * w : axisLabelW + w * 0.01 + (chart.legendPos === 'l' ? legendW : 0);
    const plotW = chart.plotLayout ? chart.plotLayout.width * w : w - plotX - w * 0.02 - (chart.legendPos === 'r' ? legendW : 0);
    const yOf = (v: number) => padTop + ((axMax - v) / range) * plotH;
    const cats = chart.series[0]?.categories ?? [];

    const gridAndAxis: React.ReactNode[] = [];
    if (chart.barDir !== 'bar') {
      // 값 축 눈금 라벨 + 수평 그리드
      ticks.forEach((t, i) => {
        const y = yOf(t);
        if (ax?.majorGridlines) {
          gridAndAxis.push(<line key={`g${i}`} x1={plotX} y1={y} x2={plotX + plotW} y2={y}
            stroke={t === 0 && !ax?.gridColor ? AXIS_COLOR : valGrid} strokeWidth={1} />);
        }
        if (showAxisLabels) {
          gridAndAxis.push(<text key={`t${i}`} x={plotX - 4} y={y + valFs * 0.35}
            fontSize={valFs} fontWeight={valWeight} fill={valColor} textAnchor="end">
            {tickStrs[i]}
          </text>);
        }
      });
      // 카테고리 축 수직 그리드 (catAx majorGridlines)
      if (chart.catAxis?.majorGridlines) {
        for (let i = 0; i <= catCount; i++) {
          const x = plotX + (i / catCount) * plotW;
          gridAndAxis.push(<line key={`vg${i}`} x1={x} y1={padTop} x2={x} y2={padTop + plotH}
            stroke={catGrid} strokeWidth={1} />);
        }
      }
      // 카테고리 라벨
      cats.forEach((c, i) => {
        gridAndAxis.push(<text key={`c${i}`} x={plotX + ((i + 0.5) / catCount) * plotW}
          y={padTop + plotH + catFs * 1.3} fontSize={catFs} fontWeight={catWeight}
          fill={catColor} textAnchor="middle">
          {c}
        </text>);
      });
    }

    /* ---- 세로 막대 -----------------------------------------------------
     * 막대폭 OOXML 계약: slot = barW × (serCount + gapW/100 − overlap/100×(serCount−1))
     * gapW 기본 150%, overlap 기본 0 */
    if (chart.type === 'bar' && chart.barDir !== 'bar') {
      const serCount = chart.series.length;
      const slotW = plotW / catCount;
      const gapW = chart.gapWidth ?? 150;
      const overlap = stacked ? 100 : chart.overlap ?? 0;
      const barW = slotW / (serCount + gapW / 100 - (overlap / 100) * (serCount - 1));
      const barX = (ci: number, si: number) =>
        plotX + ci * slotW + (gapW / 200) * barW + si * (1 - overlap / 100) * barW;
      const bars: React.ReactNode[] = [];
      const defs: React.ReactNode[] = [];
      chart.series.forEach((ser, si) => {
        // 시리즈 그라데이션 — OOXML 각도(0=→, 90=↓)를 SVG 좌표로 변환
        let fillPaint = seriesColor(chart, si, 0, accents);
        if (ser.fill?.gradient) {
          const gid = `chgrad-${uid}-${si}`;
          const stops = [...ser.fill.gradient.stops].sort((a, b) => a.position - b.position);
          const angle = ser.fill.gradient.angle ?? 90;
          const rad = (angle * Math.PI) / 180;
          const dx = Math.cos(rad) * 50;
          const dy = Math.sin(rad) * 50;
          defs.push(
            <linearGradient key={gid} id={gid}
              x1={`${50 - dx}%`} y1={`${50 - dy}%`} x2={`${50 + dx}%`} y2={`${50 + dy}%`}>
              {stops.map((s, i) => <stop key={i} offset={`${s.position}%`} stopColor={s.color} />)}
            </linearGradient>,
          );
          fillPaint = `url(#${gid})`;
        }
        ser.values.forEach((v, ci) => {
          if (v === null) return;
          const x = barX(ci, si);
          const segment = segments[si][ci]!;
          const y0 = yOf(Math.max(segment.start, segment.end));
          const bh = Math.abs(yOf(segment.start) - yOf(segment.end));
          bars.push(<rect key={`${si}_${ci}`} x={x} y={y0} width={barW}
            height={Math.max(bh, 0.5)} fill={fillPaint} />);
          if (showVal(ser)) {
            const fs = labelFs(ser, fontSize * 0.9);
            const centered = (ser.dataLabels?.position ?? chart.dataLabels?.position) === 'ctr';
            bars.push(<text key={`bl${si}_${ci}`} x={x + barW / 2} y={centered ? y0 + bh / 2 + fs * 0.35 : y0 - fs * 0.45}
              fontSize={fs} fontWeight={labelWeight(ser)}
              fill={labelColor(ser, LABEL_COLOR)} textAnchor="middle">
              {formatValue(v, labelFmt(ser))}
            </text>);
          }
        });
      });
      const legend = buildLegend(chart.series.map((s, i) => ({
        label: s.name ?? `계열 ${i + 1}`, color: seriesColor(chart, i, 0, accents),
      })));
      return [<defs key="defs">{defs}</defs>, ...gridAndAxis, ...bars, legend];
    }

    /* ---- 가로 막대 ------------------------------------------------------
     * 계약: 첫 카테고리·첫 시리즈가 아래쪽(카테고리 축 기준)에 그려지고
     * 범례는 시각적 위→아래 순(=시리즈 역순)으로 나열된다(LO 실측). */
    if (chart.type === 'bar') {
      const serCount = chart.series.length;
      // 값 축이 가로로 길어 LO 는 눈금을 촘촘히 잡는다 → 밀도 10 재계산
      const hStep = ax?.majorUnit ?? niceStep((ax?.max ?? dataMax) - (ax?.min ?? Math.min(dataMin, 0)), 10);
      const hMax = ax?.max ?? Math.ceil((dataMax * 1.0001) / hStep) * hStep;
      const hMin = ax?.min ?? Math.floor(Math.min(dataMin, 0) / hStep) * hStep;
      const hRange = hMax - hMin || 1;
      const hTicks: number[] = [];
      for (let t = hMin; t <= hMax + hStep * 0.001; t += hStep) hTicks.push(Math.round(t * 1e9) / 1e9);

      const labelW = w * 0.18;
      const hPlotX = labelW + (chart.legendPos === 'l' ? legendW : 0);
      const hPlotW = w - hPlotX - w * 0.04 - (chart.legendPos === 'r' ? legendW : 0);
      const tickLabelH = valFs * 1.6;
      const plotAreaH = h - legendH - tickLabelH;
      const slotH = (plotAreaH * 0.92) / catCount;
      const gapW = chart.gapWidth ?? 150;
      const overlap = stacked ? 100 : chart.overlap ?? 0;
      const barH = slotH / (serCount + gapW / 100 - (overlap / 100) * (serCount - 1));
      const xOf = (v: number) => hPlotX + ((v - hMin) / hRange) * hPlotW;
      const items: React.ReactNode[] = [
        <line key="ax" x1={xOf(0)} y1={legendTop + plotAreaH * 0.02} x2={xOf(0)} y2={legendTop + plotAreaH * 0.98}
          stroke={AXIS_COLOR} strokeWidth={1} />,
      ];
      hTicks.forEach((t, i) => {
        if (ax?.majorGridlines) {
          items.push(<line key={`g${i}`} x1={xOf(t)} y1={legendTop + plotAreaH * 0.02} x2={xOf(t)} y2={legendTop + plotAreaH * 0.98}
            stroke={t === 0 && !ax?.gridColor ? AXIS_COLOR : valGrid} strokeWidth={1} />);
        }
        items.push(<text key={`vt${i}`} x={xOf(t)} y={legendTop + plotAreaH + valFs * 1.1}
          fontSize={valFs} fontWeight={valWeight} fill={valColor} textAnchor="middle">
          {formatValue(t, ax?.numFmt)}
        </text>);
      });
      chart.series.forEach((ser, si) => {
        ser.values.forEach((v, ci) => {
          if (v === null) return;
          const row = catCount - 1 - ci;          // 첫 카테고리 = 맨 아래
          const sub = serCount - 1 - si;          // 첫 시리즈 = 그룹 내 아래
          const y = legendTop + plotAreaH * 0.04 + row * slotH + (gapW / 200) * barH
            + sub * (1 - overlap / 100) * barH;
          const segment = segments[si][ci]!;
          const x0 = xOf(Math.min(segment.start, segment.end));
          const bw = Math.abs(xOf(segment.end) - xOf(segment.start));
          items.push(<rect key={`${si}_${ci}`} x={x0} y={y} width={Math.max(bw, 0.5)}
            height={barH} fill={seriesColor(chart, si, ci, accents)} />);
        });
      });
      cats.forEach((c, i) => items.push(
        <text key={`l${i}`} x={labelW - 6}
          y={legendTop + plotAreaH * 0.04 + (catCount - 1 - i + 0.5) * slotH + catFs * 0.35}
          fontSize={catFs} fontWeight={catWeight} fill={catColor} textAnchor="end">{c}</text>,
      ));
      const legend = buildLegend(chart.series.map((s, i) => ({
        label: s.name ?? `계열 ${i + 1}`, color: seriesColor(chart, i, 0, accents),
      })).reverse());
      return [...items, legend];
    }

    /* ---- 라인 ----------------------------------------------------------- */
    const items: React.ReactNode[] = [...gridAndAxis];
    chart.series.forEach((ser, si) => {
      const pts: { x: number; y: number; v: number; ci: number }[] = [];
      ser.values.forEach((v, ci) => {
        if (v === null) return;
        pts.push({ x: plotX + ((ci + 0.5) / catCount) * plotW, y: yOf(v), v, ci });
      });
      if (pts.length === 0) return;
      items.push(<polyline key={`ln${si}`}
        points={pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')}
        fill="none" stroke={seriesColor(chart, si, 0, accents)}
        strokeWidth={Math.max(2, h * 0.008)} strokeLinecap="round" strokeLinejoin="round" />);
      if (showVal(ser)) {
        const fs = labelFs(ser, fontSize * 0.9);
        pts.forEach((p) => {
          // 기본 위치=포인트 오른쪽(라벨 폭 절반+간격), manualLayout 오프셋은
          // 차트영역 비율 가산 (LO 렌더 실측과 일치)
          const off = ser.dataLabels?.offsets?.[p.ci];
          const half = formatValue(p.v, labelFmt(ser)).length * fs * 0.3;
          const lx = off ? p.x + half + fs * 0.4 + off.dx * w : p.x;
          const ly = off ? p.y + off.dy * h : p.y - fs * 1.05;
          if (off && leaderOn(ser)) {
            items.push(<line key={`ldr${si}_${p.ci}`}
              x1={p.x} y1={p.y} x2={lx} y2={ly + fs * 0.6}
              stroke={LEADER_COLOR} strokeWidth={1} />);
          }
          items.push(
            <text key={`dl${si}_${p.ci}`} x={lx} y={ly + fs * 0.35}
              fontSize={fs} fontWeight={labelWeight(ser)}
              fill={labelColor(ser, LABEL_COLOR)} textAnchor="middle">
              {formatValue(p.v, labelFmt(ser))}
            </text>,
          );
        });
      }
    });
    const legend = buildLegend(chart.series.map((s, i) => ({
      label: s.name ?? `계열 ${i + 1}`, color: seriesColor(chart, i, 0, accents),
    })));
    return [...items, legend];
  }, [chart, w, h, accents, uid]);

  if (!chart || !content) {
    // 미지원 차트 종류 — 도형 크기를 유지한 연회색 자리표시자
    return (
      <div style={{ width: '100%', height: '100%', background: '#f4f4f4', border: '1px solid #e0e0e0', boxSizing: 'border-box' }} />
    );
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block', pointerEvents: 'none', fontFamily: chart.fontFamily ? resolveFontFallback(chart.fontFamily) : undefined }}
    >
      {title && (
        <text x={width / 2} y={titleFs * 1.25} fontSize={titleFs} fontWeight={600}
          fill={TITLE_COLOR} textAnchor="middle">
          {title}
        </text>
      )}
      <g transform={titleH > 0 ? `translate(0,${titleH})` : undefined}>{content}</g>
    </svg>
  );
};
